import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { EASE, HeroLines, Counter, Tilt, Marquee } from "@/components/site/motion";
import { Problems, CapabilitiesTeaser } from "@/components/home/SectionsA";
import { Stories, BestFit } from "@/components/home/SectionsB";
import { AboutPreview, FinalCTA } from "@/components/home/SectionsC";

const LEDGER = [
  { prefix: "$", value: 10, suffix: "M", label: "pipeline generated through a single channel partner in 60 days" },
  { prefix: "", value: 150, suffix: "%", label: "increase in partner program profitability" },
  { prefix: "", value: 63, suffix: "%", label: "partner-sourced revenue growth in a single year" },
  { prefix: "", value: 30, suffix: "%", label: "overall year-over-year program growth achieved during a turnaround" },
];

const MARQUEE_ITEMS = [
  { text: "Build", brass: true },
  { text: "Fix", brass: true },
  { text: "Monetize", brass: true },
  { text: "Strategy & Opportunity" },
  { text: "Program Design & Economics" },
  { text: "Operating Model" },
  { text: "GTM & Activation" },
  { text: "Performance & Scale" },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const ledgerY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const copyY = useTransform(scrollYProgress, [0, 1], [0, -40]);

  return (
    <>
      <section className="hero" ref={heroRef} data-testid="hero-section">
        <div className="wrap hero-grid">
          <motion.div className="hero-copy" style={{ y: copyY }}>
            <motion.span
              className="kicker"
              data-testid="hero-kicker"
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: EASE }}
            >
              Fractional Head of Partnerships
            </motion.span>
            <HeroLines
              lines={[
                { text: "Build.", verb: true },
                { text: "Fix.", verb: true },
                { text: "Monetize.", verb: true },
                { text: "Partner Programs That Produce Revenue." },
              ]}
            />
            <motion.p
              className="lede"
              data-testid="hero-subhead"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.75, ease: EASE }}
            >
              I turn new and existing partnerships into pipeline, revenue, and market expansion through hands-on
              fractional or interim leadership.
            </motion.p>
            <motion.div
              className="btn-row"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.95, ease: EASE }}
            >
              <Link to="/contact" className="btn btn-primary" data-testid="hero-cta-primary">
                Discuss a Partnership Challenge <span className="btn-arrow">→</span>
              </Link>
              <a
                href="#results"
                className="btn btn-ghost"
                data-testid="hero-cta-secondary"
                onClick={(e) => {
                  e.preventDefault();
                  const el = document.querySelector("#results");
                  if (el && window.__lenis) window.__lenis.scrollTo(el, { offset: -90 });
                  else if (el) el.scrollIntoView({ behavior: "smooth" });
                }}
              >
                See the Track Record
              </a>
            </motion.div>
          </motion.div>

          <motion.div style={{ y: ledgerY }} className="ledger-wrap">
            <Tilt>
              <motion.aside
                className="ledger"
                data-testid="career-ledger"
                initial={{ opacity: 0, y: 40, rotateX: 4 }}
                animate={{ opacity: 1, y: 0, rotateX: 0 }}
                transition={{ duration: 1.1, delay: 0.5, ease: EASE }}
              >
                <div className="ledger-head">
                  <span>Selected results</span>
                </div>
                {LEDGER.map((row, i) => (
                  <motion.div
                    className="ledger-row"
                    key={row.label}
                    data-testid={`ledger-row-${i}`}
                    initial={{ opacity: 0, x: 24 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.7, delay: 0.85 + i * 0.14, ease: EASE }}
                  >
                    <Counter prefix={row.prefix} value={row.value} suffix={row.suffix} className="ledger-figure" />
                    <span className="ledger-label">{row.label}</span>
                  </motion.div>
                ))}
              </motion.aside>
            </Tilt>
          </motion.div>
        </div>
      </section>

      <Marquee items={MARQUEE_ITEMS} />

      <Problems />
      <CapabilitiesTeaser />
      <Stories />
      <BestFit />
      <AboutPreview />
      <FinalCTA />
    </>
  );
}
